import {
  getReportsFindAllQueryKey,
  getReportsFindOneQueryKey,
  ReportResponseDtoStatus,
  useReportsRemove,
} from '@/api/WhisApi/generated';
import {
  AlertDialog,
  type AlertDialogAction,
} from '@/components/AlertDialog/AlertDialog';
import { Button } from '@/components/Button/Button';
import { EmptyStateView } from '@/components/EmptyStateView/EmptyStateView';
import ReportCard from '@/components/ReportList/ReportCard/ReportCard';
import { ScreenHeader } from '@/components/ScreenHeader';
import { TabScreenContainer } from '@/components/TabScreenContainer';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { BottomButtonBar } from '@/screens/ReportCreation/components/BottomButtonBar';
import { useReportSubmission } from '@/screens/ReportCreation/hooks/useReportSubmission';
import { useReportSubmissionNavigation } from '@/screens/ReportCreation/hooks/useReportSubmissionNavigation';
import {
  SIMILAR_REPORT_CARD_MAX_WIDTH,
  SIMILAR_REPORT_CARD_MIN_WIDTH,
} from '@/screens/SimilarReports/similarReports.constants';
import { canDeleteReportDraft } from '@/utils/userPermissions';
import { Ionicons } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter, useSegments } from 'expo-router';
import React from 'react';
import { ScrollView } from 'react-native';
import { Separator, SizableText, useTheme, XStack, YStack } from 'tamagui';
import { useSimilarReportsStore } from '@/store/similarReports/similarReports.store';

export default function SimilarReportsScreen(): React.ReactNode {
  const { t } = useAppTranslation();
  const theme = useTheme();
  const { isSmallScreen, isMediumScreen, isLargeScreen } = useBreakpoint();
  const router = useRouter();
  const segments = useSegments() as string[];
  const { reportId: editedReportId } = useLocalSearchParams<{
    reportId?: string;
  }>();
  const queryClient = useQueryClient();
  const state = useSimilarReportsStore((store) => store.state);
  const setState = useSimilarReportsStore((store) => store.setState);
  const { user } = useAuth();
  const isBoardRoute = segments.includes('board');
  const isEditRoute = segments.includes('edit');
  const isEditContext = isEditRoute && Boolean(editedReportId);
  const { mutate: removeReport, isPending: isRemoving } = useReportsRemove();
  const { isSubmitting, submitReport: submitReportHandler } =
    useReportSubmission({
      reportCode: state?.reportCode,
    });

  const warningColor = theme.warningFont?.val ?? theme.color?.val;
  const reportDetailPathname = isBoardRoute
    ? '/(tabs)/board/reports/[reportId]'
    : '/(tabs)/reports/[reportId]';
  const reportsListPath = isBoardRoute ? '/(tabs)/board' : '/(tabs)/reports';
  const { handleSubmitError, handleSubmissionSuccess, handleQueuedSuccess } =
    useReportSubmissionNavigation({
      isBoardRoute,
      isEditContext,
      editedReportId,
      onBeforeNavigate: (): void => {
        setState(null);
      },
    });

  const canDeleteDraft = (): boolean => {
    if (!isEditContext) {
      return true;
    }

    if (!state?.report) {
      return false;
    }

    if (state.report.status !== ReportResponseDtoStatus.draft) {
      return false;
    }

    return canDeleteReportDraft(state.report, user);
  };

  const handleBack = (): void => {
    setState(null);
    router.back();
  };

  const handleDeleteDraft = (): void => {
    if (isEditContext && editedReportId) {
      removeReport(
        { reportId: editedReportId },
        {
          onSuccess: () => {
            setState(null);
            queryClient.invalidateQueries({
              queryKey: getReportsFindAllQueryKey(),
            });
            queryClient.invalidateQueries({
              queryKey: getReportsFindOneQueryKey(editedReportId),
            });
            router.replace(reportsListPath);
          },
          onError: handleSubmitError,
        },
      );
      return;
    }

    setState(null);
    router.replace('/(tabs)/report-creation?reset=true');
  };

  const submitReport = async (mode: 'draft' | 'published'): Promise<void> => {
    if (!state) {
      return;
    }

    await submitReportHandler(state.pendingValues, mode, {
      onSuccess: (createdReport): void => {
        handleSubmissionSuccess(mode, createdReport.id);
      },
      onQueued: handleQueuedSuccess,
      onError: handleSubmitError,
    });
  };

  const handleSelectReport = (reportId: string): void => {
    const pathname = isEditContext
      ? reportDetailPathname
      : '/(tabs)/report-creation/similar/[reportId]';

    router.push({
      pathname,
      params: { reportId },
    });
  };
  const deleteDraftActions: AlertDialogAction[] = [
    {
      label: t('buttons.cancel'),
      actionType: 'cancel',
    },
    {
      label: t('reportCreation.deleteDraftDialog.confirm'),
      actionType: 'action',
      variant: 'destructive',
      onPress: handleDeleteDraft,
    },
  ];

  const deleteDraftButton = canDeleteDraft() ? (
    <AlertDialog
      title={t('reportCreation.deleteDraftDialog.title')}
      description={t('reportCreation.deleteDraftDialog.description')}
      actions={deleteDraftActions}
    >
      <Button
        title={
          isSmallScreen ? undefined : t('reportCreation.buttons.deleteDraft')
        }
        icon={
          isSmallScreen ? (
            <Ionicons
              name="trash-outline"
              size={20}
              color={theme.$error?.val}
            />
          ) : undefined
        }
        variant="outlined-destructive"
        disabled={isRemoving}
      />
    </AlertDialog>
  ) : null;

  if (!state) {
    return (
      <TabScreenContainer>
        <EmptyStateView title={t('noData')} />
      </TabScreenContainer>
    );
  }

  if (!state.reports.length) {
    return (
      <TabScreenContainer>
        <EmptyStateView title={t('noData')} />
      </TabScreenContainer>
    );
  }

  const reportColumns = isLargeScreen ? 3 : isMediumScreen ? 2 : 1;
  const reportCardFlexBasis = `${100 / reportColumns}%`;

  return (
    <TabScreenContainer>
      <YStack flex={1}>
        {!isSmallScreen && (
          <YStack marginBottom="$true">
            <ScreenHeader title={t('reportCreation.similarReports.title')} />
          </YStack>
        )}

        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            flexGrow: 1,
          }}
        >
          <YStack width="100%" gap="$true">
            <YStack gap="$2">
              <XStack alignItems="center" gap="$2">
                <Ionicons
                  name="warning-outline"
                  size={20}
                  color={warningColor}
                />
                <SizableText fontFamily="$bold" size="$h6">
                  {t('reportCreation.similarReports.heading')}
                </SizableText>
              </XStack>
              {isSmallScreen && <Separator />}
              <SizableText color="$secondary">
                {t('reportCreation.similarReports.subheading')}
              </SizableText>
            </YStack>

            <XStack flexWrap="wrap" gap="$true">
              {state.reports.map((report) => (
                <YStack
                  key={report.id}
                  flexBasis={reportCardFlexBasis}
                  flexGrow={1}
                  minWidth={
                    isSmallScreen ? '100%' : SIMILAR_REPORT_CARD_MIN_WIDTH
                  }
                  maxWidth={
                    isSmallScreen ? '100%' : SIMILAR_REPORT_CARD_MAX_WIDTH
                  }
                >
                  <ReportCard
                    report={report}
                    onPress={() => handleSelectReport(report.id)}
                  />
                </YStack>
              ))}
            </XStack>
          </YStack>
        </ScrollView>

        <BottomButtonBar
          showBackButton={isLargeScreen}
          onBack={handleBack}
          leftAccessory={isSmallScreen ? deleteDraftButton : undefined}
          rightLeadingAction={!isSmallScreen ? deleteDraftButton : undefined}
          showSaveAsDraft
          onSaveAsDraft={() => submitReport('draft')}
          primaryButtonLabel={t('reportCreation.buttons.publish')}
          onPrimaryAction={() => submitReport('published')}
          primaryButtonLoading={isSubmitting}
        />
      </YStack>
    </TabScreenContainer>
  );
}
